/**
 * Third discount UX state: a code the platform ACCEPTS but that allocates
 * nothing (valid product-scoped code, only ineligible items in cart).
 * Horizon covers applied (pill + discount line) and rejected
 * (applicable === false -> error text) — but an accepted zero-allocation
 * code renders neither: cart-summary.liquid builds its pill list from
 * allocations, so the apply looks like a silent no-op.
 *
 * Detection is read-only against /cart.js: an entry in discount_codes with
 * applicable === true while total_discount is 0. (An automatic discount
 * running at the same time would mask this — acceptable, the message would
 * be ambiguous there anyway.) The note is re-evaluated after every
 * discount/lines update and once on load, since the code persists in the
 * cart across reloads with no visible trace.
 */

const NOTE_CLASS = 'blessac-discount-note';

let evaluating = false;

async function evaluate() {
  if (evaluating) return;
  const hosts = document.querySelectorAll('cart-discount-component');
  if (hosts.length === 0) return;

  evaluating = true;
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();

    const acceptedCodes = (cart.discount_codes || [])
      .filter((entry) => entry && entry.applicable === true)
      .map((entry) => entry.code);
    const zeroAllocation = cart.item_count > 0 && acceptedCodes.length > 0 && cart.total_discount === 0;

    for (const host of document.querySelectorAll('cart-discount-component')) {
      let note = host.querySelector(`.${NOTE_CLASS}`);
      if (!zeroAllocation) {
        note?.remove();
        continue;
      }
      if (!note) {
        note = document.createElement('p');
        note.className = NOTE_CLASS;
        note.setAttribute('role', 'status');
        host.append(note);
      }
      note.textContent = `${acceptedCodes.join(', ')} doesn’t apply to items already on sale.`;
    }
  } catch {
    // Network hiccup: leave the current note state untouched.
  } finally {
    evaluating = false;
  }
}

function onCartEvent(event) {
  // The component dispatches before its fetch, carrying the request promise;
  // settle after it (and after morphSection, which runs in the same
  // continuation) so we read the post-update cart and the post-morph DOM.
  const settle = event?.promise instanceof Promise ? event.promise.catch(() => {}) : Promise.resolve();
  settle.then(() => evaluate());
}

// Capture phase: reaches us whether or not the standard events bubble.
document.addEventListener('shopify:cart:discount-update', onCartEvent, true);
document.addEventListener('shopify:cart:lines-update', onCartEvent, true);
document.addEventListener('shopify:section:load', () => evaluate());

evaluate();
